import { zodResolver } from '@hookform/resolvers/zod';
import { type LoginInput, loginSchema } from '@mm/shared';
import { browserSupportsWebAuthn } from '@simplewebauthn/browser';
import { Fingerprint, LineChart } from 'lucide-react';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { ApiError } from '../../api/client';
import { Button } from '../../components/ui/Button';
import { Alert } from '../../components/ui/display';
import { Field, Input } from '../../components/ui/inputs';
import { useAuth } from './AuthProvider';

export function LoginPage() {
  const { status, login, loginWithPasskey } = useAuth();
  const [passkeyBusy, setPasskeyBusy] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const [error, setError] = useState<string | null>(null);
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<LoginInput>({
    resolver: zodResolver(loginSchema),
    defaultValues: { email: '', password: '' },
  });

  const from = (location.state as { from?: string } | null)?.from ?? '/';
  if (status === 'authenticated' || status === 'locked') return <Navigate to={from} replace />;

  const signInWithPasskey = async () => {
    setError(null);
    setPasskeyBusy(true);
    try {
      await loginWithPasskey();
      navigate(from, { replace: true });
    } catch (err) {
      setError(
        err instanceof ApiError
          ? err.message
          : err instanceof Error && err.name === 'NotAllowedError'
            ? 'Fingerprint / Face ID was cancelled.'
            : 'Fingerprint / Face ID sign-in is not available here.',
      );
    } finally {
      setPasskeyBusy(false);
    }
  };

  const onSubmit = handleSubmit(async (values) => {
    setError(null);
    try {
      await login(values.email, values.password);
      navigate(from, { replace: true });
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Sign-in failed. Please try again.');
    }
  });

  return (
    <div className="flex min-h-screen items-center justify-center bg-slate-50 px-4">
      <div className="w-full max-w-sm">
        <div className="mb-8 flex flex-col items-center text-center">
          <div className="mb-3 rounded-xl bg-brand-600 p-2.5 text-white shadow-sm">
            <LineChart className="size-6" aria-hidden />
          </div>
          <h1 className="text-xl font-semibold text-slate-900">Marketing &amp; P/L Manager</h1>
          <p className="mt-1 text-sm text-slate-500">Sign in to continue</p>
        </div>
        <form
          onSubmit={onSubmit}
          noValidate
          className="space-y-4 rounded-xl border border-slate-200 bg-white p-6 shadow-sm"
        >
          {error && <Alert tone="error">{error}</Alert>}
          <Field label="Email" htmlFor="email" error={errors.email?.message}>
            <Input
              id="email"
              type="email"
              autoComplete="username"
              autoFocus
              invalid={!!errors.email}
              {...register('email')}
            />
          </Field>
          <Field label="Password" htmlFor="password" error={errors.password?.message}>
            <Input
              id="password"
              type="password"
              autoComplete="current-password"
              invalid={!!errors.password}
              {...register('password')}
            />
          </Field>
          <Button type="submit" className="w-full" loading={isSubmitting}>
            Sign in
          </Button>
          {browserSupportsWebAuthn() && (
            <Button
              type="button"
              variant="secondary"
              className="w-full"
              icon={<Fingerprint className="size-4" />}
              loading={passkeyBusy}
              onClick={() => void signInWithPasskey()}
            >
              Sign in with fingerprint / Face ID
            </Button>
          )}
        </form>
      </div>
    </div>
  );
}
